/**
 * Site variants (white label). The variant is chosen at build time with
 * VITE_SITE_VARIANT (`tipa` by default, `oti-ouest` for the Office de tourisme de
 * l'Ouest): same source, same data, same API; only the configuration below changes.
 * Pure data: this module is also imported by scripts/postbuild.ts under Node.
 */
import type { LatLng } from '@shared/model/types';

export type SiteVariantId = 'tipa' | 'oti-ouest';
export type PageKind = 'itinerary' | 'trails' | 'about';

export interface PageConfig {
    path: string;
    /** `<title>` and h1 of the page. */
    title: string;
    /** Label in the header, defaults to `title`. */
    menuTitle?: string;
    description: string;
    kind: PageKind;
    /** Shown in the header navigation. */
    inMenu: boolean;
    /** Coarse location listed first in the selects. */
    area?: string;
    center: LatLng;
    zoom: number;
    defaultStart?: string;
    defaultEnd?: string;
    /** Draw every trail on the map when the page loads. */
    showAllRoutes?: boolean;
    /** Home page: hero text above the form. */
    hero?: boolean;
}

export interface SiteVariant {
    id: SiteVariantId;
    /** Full name, used in `<title>` ("Page | name"). */
    name: string;
    /** Title of the home page. */
    homeTitle: string;
    /** Label next to the logo in the header. */
    headerLabel: string;
    logo: string;
    logoHeight: number;
    /** Canonical URL of the production site (no trailing slash). */
    siteUrl: string;
    /**
     * Google tag ID loaded by gtag.js: each variant reports to its own GA4 property. A G- ID works
     * only when it is the ID of a Google tag, not just a destination of one (Tag Manager > Google tags).
     */
    gaMeasurementId: string;
    /** Icons and share image (paths under public/), written in the `<head>` by vite.config.ts. */
    favicon: string;
    /** 512 px square PNG (apple-touch-icon). */
    touchIcon: string;
    /** og:image and logo of the JSON-LD publisher. */
    shareImage: string;
    /** Publisher in the JSON-LD of the home page. */
    publisher: string;
    email: string;
    /** When false, every link to another site is rendered as plain text (white label). */
    externalLinks: boolean;
    /** 404 page: link to Wikipedia's Mafate article, or back to the home page. */
    notFoundLink: 'wikipedia' | 'home';
    pages: PageConfig[];
}

export const SITE_EMAIL = 'contact@tipa.re';
export const ONF_CLOSED_TRAILS_URL =
    'https://www.onf.fr/vivre-la-foret/+/b90::randonnee-la-reunion-connaitre-les-sentiers-fermes.html';

const ISLAND_CENTER: LatLng = [-21.1159, 55.5132];

const MAFATE: PageConfig = {
    path: '/mafate/',
    title: 'Mafate',
    description:
        'Calculez votre itinéraire de randonnée dans le cirque de Mafate : sentiers ouverts et fermés par l’ONF, distances, dénivelés et durées.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Mafate',
    center: [-21.0605, 55.4149],
    zoom: 12,
    defaultStart: 'parking-col-des-boeufs',
    defaultEnd: 'la-nouvelle',
};
const CILAOS: PageConfig = {
    path: '/cilaos/',
    title: 'Cilaos',
    description:
        'Calculez votre itinéraire de randonnée dans le cirque de Cilaos et vers le Piton des Neiges : sentiers ouverts et fermés par l’ONF.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Cilaos',
    center: [-21.1387, 55.4565],
    zoom: 11.5,
    defaultStart: 'le-bloc-cilaos',
    defaultEnd: 'piton-des-neiges',
};
const SALAZIE: PageConfig = {
    path: '/salazie/',
    title: 'Salazie',
    description:
        'Calculez votre itinéraire de randonnée dans le cirque de Salazie, de Hell-Bourg à Bélouve : sentiers ouverts et fermés par l’ONF.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Salazie',
    center: [-21.0453, 55.5005],
    zoom: 12,
    defaultStart: 'hell-bourg',
    defaultEnd: 'gite-belouve',
};
const VOLCAN: PageConfig = {
    path: '/volcan/',
    title: 'Volcan – Piton de la Fournaise',
    menuTitle: 'Volcan',
    description:
        'Calculez votre itinéraire de randonnée au Piton de la Fournaise depuis le Pas de Bellecombe : sentiers ouverts et fermés par l’ONF.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Piton de la Fournaise',
    center: [-21.256307, 55.640924],
    zoom: 10,
    defaultStart: 'pas-de-bellecombe-jacob',
    defaultEnd: 'piton-de-la-fournaise',
};
const COTE_OUEST: PageConfig = {
    path: '/cote-ouest/',
    title: 'Côte Ouest',
    description:
        'Calculez votre itinéraire de randonnée sur la côte Ouest de la Réunion, de Saint-Paul à Saint-Leu : sentiers ouverts et fermés par l’ONF.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Saint-Paul',
    center: [-21.0553, 55.2853],
    zoom: 12,
    defaultStart: 'debut-chemin-ravine-bernica',
    defaultEnd: 'eglise-st-joseph-artisan',
};
const HAUTS_DE_L_OUEST: PageConfig = {
    path: '/hauts-de-l-ouest/',
    title: 'Hauts de l’Ouest',
    description:
        'Calculez votre itinéraire de randonnée dans les Hauts de l’Ouest de la Réunion, du Maïdo au Grand Bénare : sentiers ouverts et fermés par l’ONF.',
    kind: 'itinerary',
    inMenu: true,
    area: 'Saint-Paul',
    center: [-21.0704, 55.3663],
    zoom: 12,
    defaultStart: 'grand-benare',
    defaultEnd: 'sans-souci',
};
const SENTIERS: PageConfig = {
    path: '/sentiers/',
    title: 'Liste des sentiers de randonnée à la Réunion',
    menuTitle: 'Carte',
    description:
        'Tous les sentiers de randonnée de la Réunion sur une carte, avec leur statut ONF (ouvert ou fermé), distance, dénivelé et durée.',
    kind: 'trails',
    inMenu: true,
    center: ISLAND_CENTER,
    zoom: 10,
};
const ABOUT: PageConfig = {
    path: '/à-propos/',
    title: 'À propos – Mentions légales',
    menuTitle: 'À propos',
    description: 'Sources des données (IGN, ONF, OpenStreetMap), sécurité sur les sentiers et mentions légales.',
    kind: 'about',
    inMenu: false,
    center: ISLAND_CENTER,
    zoom: 10,
};

export const TIPA: SiteVariant = {
    id: 'tipa',
    name: 'Tipa.re',
    homeTitle: 'Tipa.re – La carte des chemins de randonnée à Mafate et à la Réunion',
    headerLabel: 'Tipa.re',
    logo: '/assets/logo-tipa-re.svg',
    logoHeight: 25,
    siteUrl: 'https://www.tipa.re',
    gaMeasurementId: 'G-B6B5XNRTQ3',
    favicon: '/favicon.ico',
    touchIcon: '/assets/logo-tipa-re-squarred.png',
    shareImage: '/assets/logo-tipa-re.png',
    publisher: 'SysDevRun',
    email: SITE_EMAIL,
    externalLinks: true,
    notFoundLink: 'wikipedia',
    pages: [
        {
            path: '/',
            title: 'La Réunion',
            menuTitle: 'Tipa.re',
            description:
                'Trouvez votre itinéraire de randonnée à Mafate et à la Réunion : tous les sentiers ONF, ouverts et fermés, mis à jour chaque jour.',
            kind: 'itinerary',
            inMenu: true,
            area: 'Mafate',
            center: ISLAND_CENTER,
            zoom: 10,
            showAllRoutes: true,
            hero: true,
        },
        MAFATE,
        CILAOS,
        SALAZIE,
        VOLCAN,
        SENTIERS,
        ABOUT,
    ],
};

/** White label for the Office de tourisme de l'Ouest: west-coast pages, no external links. */
export const OTI_OUEST: SiteVariant = {
    id: 'oti-ouest',
    name: 'Sentiers – Office de tourisme de l’Ouest, La Réunion',
    homeTitle: 'Sentiers – Office de tourisme de l’Ouest, La Réunion',
    headerLabel: 'Office de tourisme de l’Ouest',
    logo: '/assets/logo-oti-ouest.svg',
    logoHeight: 80,
    siteUrl: 'https://oti-ouest.tipa.re',
    // Google tag "Tipa OTI Ouest", destination G-W7XZ9MESB3 (stream oti-ouest.tipa.re)
    gaMeasurementId: 'GT-5DF67CVX',
    favicon: '/assets/logo-oti-ouest.svg',
    touchIcon: '/assets/logo-oti-ouest-squarred.png',
    shareImage: '/assets/logo-oti-ouest-squarred.png',
    publisher: 'Office de tourisme de l’Ouest',
    email: SITE_EMAIL,
    externalLinks: false,
    notFoundLink: 'home',
    pages: [
        {
            path: '/',
            title: 'La Réunion',
            menuTitle: 'Accueil',
            description:
                'Trouvez votre itinéraire de randonnée dans l’Ouest de la Réunion : tous les sentiers ONF, ouverts et fermés, mis à jour chaque jour.',
            kind: 'itinerary',
            inMenu: true,
            area: 'Mafate',
            center: [-21.0409, 55.3811],
            zoom: 11,
            showAllRoutes: true,
            hero: false,
        },
        MAFATE,
        { ...CILAOS, inMenu: false },
        { ...SALAZIE, inMenu: false },
        { ...VOLCAN, inMenu: false },
        COTE_OUEST,
        HAUTS_DE_L_OUEST,
        SENTIERS,
        ABOUT,
    ],
};

export const VARIANTS: Record<SiteVariantId, SiteVariant> = { tipa: TIPA, 'oti-ouest': OTI_OUEST };

export function siteVariant(id: string | undefined): SiteVariant {
    if (!id || id === 'tipa') return TIPA;
    const variant = VARIANTS[id as SiteVariantId];
    if (!variant) throw new Error(`Unknown VITE_SITE_VARIANT "${id}" (expected ${Object.keys(VARIANTS).join(' | ')})`);
    return variant;
}
